/**
 * Pool Reports Offhire Vessel Breakdown Page
 *
 * Displays detailed offhire breakdown for a specific vessel.
 * Shows individual offhire events with descriptions and fuel costs.
 * UI matches Angular implementation exactly.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download } from 'lucide-react';
import { Button, Skeleton } from '@/components/ui';
import { useOffhireVesselBreakdown } from '@/hooks/usePoolReports';
import { usePoolReportsStore } from '@/store';
import { CSVService } from '@/utils/csvExport';
import { formatNumber } from '@/utils/formatters';
import type { Offhire } from '@/types/poolReports.types';
import {
  POOL_REPORT_VESSEL_COL_HEADER,
  poolReportThGroupSideClass,
} from '@/components/poolReports/poolReportTableChrome';

interface HiddenFuelTypes {
  HSFO: boolean;
  MOGO: boolean;
  LSFO: boolean;
  LSGO: boolean;
}

export function PoolReportsOffhireVesselBreakdownPage() {
  const { ownerId, vesselId } = useParams<{ ownerId: string; vesselId: string }>();
  const [searchParams] = useSearchParams();
  const { reportType, setSubTitle } = usePoolReportsStore();

  const fileId = Number(searchParams.get('fileId'));
  const poolId = Number(searchParams.get('poolId'));
  const ownerName = searchParams.get('ownerName') || 'Owner';
  const vesselName = searchParams.get('vesselName') || 'Vessel';
  const numericVesselId = vesselId ? Number(vesselId) : 0;

  const { data: vesselBreakdownData, isLoading: isVesselBreakdownLoading } =
    useOffhireVesselBreakdown(fileId, numericVesselId, Boolean(fileId && vesselId));
  const offhires: Offhire[] = useMemo(() => vesselBreakdownData || [], [vesselBreakdownData]);
  const isLoading = isVesselBreakdownLoading;
  const [hiddenFuelTypes, setHiddenFuelTypes] = useState<HiddenFuelTypes>({
    HSFO: false,
    MOGO: false,
    LSFO: false,
    LSGO: false,
  });

  // Set subtitle for navigation
  useEffect(() => {
    setSubTitle({
      value: `Offhire Breakdown for: ${vesselName}`,
      backLink: `/reports/${reportType}/offhire/owner/${ownerId}`,
      queryParams: {
        poolId: poolId.toString(),
        fileId: fileId.toString(),
        ownerName,
      },
    });

    return () => {
      setSubTitle(null);
    };
  }, [vesselName, ownerId, reportType, poolId, fileId, ownerName, setSubTitle]);

  // Calculate which fuel types to suppress (hide columns where all values are 0)
  useEffect(() => {
    if (offhires.length === 0) {
      return;
    }

    const totals = {
      HSFO: 0,
      MOGO: 0,
      LSFO: 0,
      LSGO: 0,
    };

    offhires.forEach((item) => {
      totals.HSFO += (item.Fuel || 0) + (item.FuelPrice || 0) + (item.FuelSum || 0);
      totals.MOGO += (item.Diesel || 0) + (item.DieselPrice || 0) + (item.DieselSum || 0);
      totals.LSFO += (item.LSFO || 0) + (item.LSFOPrice || 0) + (item.LSFOSum || 0);
      totals.LSGO += (item.LSGO || 0) + (item.LSGOPrice || 0) + (item.LSGOSum || 0);
    });

    setHiddenFuelTypes({
      HSFO: totals.HSFO === 0,
      MOGO: totals.MOGO === 0,
      LSFO: totals.LSFO === 0,
      LSGO: totals.LSGO === 0,
    });
  }, [offhires]);

  const handleExportCSV = () => {
    if (offhires.length === 0) return;

    const headers: { [key: string]: string } = {
      VesselName: '',
      Days: 'Days',
      Hours: 'Hours',
      Minutes: 'Minutes',
    };

    if (!hiddenFuelTypes.HSFO) {
      headers.Fuel = 'HSFO MT';
      headers.FuelPrice = 'HSFO Price $/MT';
      headers.FuelSum = 'HSFO $';
    }
    if (!hiddenFuelTypes.MOGO) {
      headers.Diesel = 'MO/GO MT';
      headers.DieselPrice = 'MO/GO Price $/MT';
      headers.DieselSum = 'MO/GO $';
    }
    if (!hiddenFuelTypes.LSFO) {
      headers.LSFO = 'LSFO MT';
      headers.LSFOPrice = 'LSFO Price $/MT';
      headers.LSFOSum = 'LSFO $';
    }
    if (!hiddenFuelTypes.LSGO) {
      headers.LSGO = 'LSGO MT';
      headers.LSGOPrice = 'LSGO Price $/MT';
      headers.LSGOSum = 'LSGO $';
    }

    headers.Lumpsum = 'Lumpsum';
    headers.Total = 'Total';
    headers.Description = 'Description';

    const excludeFields = [
      'OwnerID',
      'ReportDisplayOrder',
      'VesselID',
      'OwnerName',
      'PortCost',
      'Estimated',
    ];
    const heading = Object.keys(headers).filter((key) => !excludeFields.includes(key));

    const reportRows = offhires.map((item: Offhire) =>
      heading.map((key) => {
        const value = item[key as keyof Offhire];
        if (value === null || value === undefined) return '';
        if (typeof value === 'number') {
          // Apply appropriate decimal places based on column
          if (key.includes('Price')) return formatNumber(value, 2, 2);
          if (
            key.includes('Fuel') ||
            key.includes('Diesel') ||
            key.includes('LSFO') ||
            key.includes('LSGO')
          ) {
            return formatNumber(value, 1, 1);
          }
          return formatNumber(value, 0, 0);
        }
        return String(value);
      })
    );

    const fileName = `offhire-report-breakdown-${vesselName}`;
    CSVService.convert(
      reportRows,
      heading.map((key) => headers[key] || key)
    ).download(fileName);
  };

  return (
    <div className="space-y-4">
      {/* Table - Angular Style with Multi-Row Header */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800">
        {/* Table Header with Export Button */}
        <div className="flex items-center justify-end border-b border-gray-200 px-6 py-4 dark:border-dark-700">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCSV}
            leftIcon={<Download className="h-4 w-4" />}
            disabled={isLoading || offhires.length === 0}
            className="rounded-full px-4 shadow-sm"
          >
            Export to Excel
          </Button>
        </div>

        <div className="report-table-shell">
          <table className="report-table">
            {isLoading ? (
              <>
                {/* Loading skeleton */}
                <thead className="bg-gray-50 dark:bg-dark-800">
                  <tr>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <th
                        key={i}
                        className="border-b border-gray-200 px-2.5 py-2 text-right dark:border-dark-700 sm:px-3 sm:py-2.5"
                      >
                        <Skeleton className="ml-auto h-6 w-20" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                  {Array.from({ length: 5 }).map((_, rowIdx) => (
                    <tr key={rowIdx}>
                      {Array.from({ length: 12 }).map((_, colIdx) => (
                        <td key={colIdx} className="px-2.5 py-2 sm:px-3 sm:py-2.5">
                          <Skeleton className="h-10 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </>
            ) : (
              <>
                {/* Multi-Row Header */}
                <thead className="bg-gray-50 dark:bg-dark-800">
                  <tr>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      {POOL_REPORT_VESSEL_COL_HEADER}
                    </th>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      DAYS
                    </th>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      HOURS
                    </th>
                    <th
                      rowSpan={2}
                      className={clsx(
                        poolReportThGroupSideClass,
                        'border-r border-gray-200 dark:border-dark-700'
                      )}
                    >
                      MINUTES
                    </th>

                    {/* HSFO Group */}
                    {!hiddenFuelTypes.HSFO && (
                      <th
                        colSpan={3}
                        className="border-b border-l border-r border-gray-200 px-2.5 py-2 text-center text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5"
                      >
                        HSFO
                      </th>
                    )}

                    {/* MO/GO Group */}
                    {!hiddenFuelTypes.MOGO && (
                      <th
                        colSpan={3}
                        className="border-b border-l border-r border-gray-200 px-2.5 py-2 text-center text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5"
                      >
                        MO/GO
                      </th>
                    )}

                    {/* LSFO Group */}
                    {!hiddenFuelTypes.LSFO && (
                      <th
                        colSpan={3}
                        className="border-b border-l border-r border-gray-200 px-2.5 py-2 text-center text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5"
                      >
                        LSFO
                      </th>
                    )}

                    {/* LSGO Group */}
                    {!hiddenFuelTypes.LSGO && (
                      <th
                        colSpan={3}
                        className="border-b border-l border-r border-gray-200 px-2.5 py-2 text-center text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5"
                      >
                        LSGO
                      </th>
                    )}

                    <th
                      rowSpan={2}
                      className="border-b border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5"
                    >
                      LUMPSUM
                    </th>
                    <th
                      rowSpan={2}
                      className="border-b border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5"
                    >
                      TOTAL
                    </th>
                    <th
                      rowSpan={2}
                      className="border-b border-gray-200 px-2.5 py-2 text-left text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5"
                    >
                      DESCRIPTION
                    </th>
                  </tr>
                  <tr>
                    {/* HSFO Sub-headers */}
                    {!hiddenFuelTypes.HSFO && (
                      <>
                        <th className="border-b border-l border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          MT
                        </th>
                        <th className="border-b border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          $ / MT
                        </th>
                        <th className="border-b border-r border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          $
                        </th>
                      </>
                    )}

                    {/* MO/GO Sub-headers */}
                    {!hiddenFuelTypes.MOGO && (
                      <>
                        <th className="border-b border-l border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          MT
                        </th>
                        <th className="border-b border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          $ / MT
                        </th>
                        <th className="border-b border-r border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          $
                        </th>
                      </>
                    )}

                    {/* LSFO Sub-headers */}
                    {!hiddenFuelTypes.LSFO && (
                      <>
                        <th className="border-b border-l border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          MT
                        </th>
                        <th className="border-b border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          $ / MT
                        </th>
                        <th className="border-b border-r border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          $
                        </th>
                      </>
                    )}

                    {/* LSGO Sub-headers */}
                    {!hiddenFuelTypes.LSGO && (
                      <>
                        <th className="border-b border-l border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          MT
                        </th>
                        <th className="border-b border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          $ / MT
                        </th>
                        <th className="border-b border-r border-gray-200 px-2.5 py-2 text-right text-base font-semibold text-gray-700 dark:border-dark-700 dark:text-gray-300 sm:px-3 sm:py-2.5">
                          $
                        </th>
                      </>
                    )}
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                  {offhires?.map((offhire, idx) => {
                    const isTotalRow = idx === offhires.length - 1;

                    return (
                      <tr key={idx} className={isTotalRow ? 'font-semibold' : ''}>
                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {offhire.VesselName}
                        </td>
                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {offhire.Days}
                        </td>
                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {offhire.Hours}
                        </td>
                        <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                          {offhire.Minutes}
                        </td>

                        {/* HSFO Columns */}
                        {!hiddenFuelTypes.HSFO && (
                          <>
                            <td className="border-l border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.Fuel, 1, 1)}
                            </td>
                            <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.FuelPrice, 2, 2)}
                            </td>
                            <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.FuelSum, 0, 0)}
                            </td>
                          </>
                        )}

                        {/* MO/GO Columns */}
                        {!hiddenFuelTypes.MOGO && (
                          <>
                            <td className="border-l border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.Diesel, 1, 1)}
                            </td>
                            <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.DieselPrice, 2, 2)}
                            </td>
                            <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.DieselSum, 0, 0)}
                            </td>
                          </>
                        )}

                        {/* LSFO Columns */}
                        {!hiddenFuelTypes.LSFO && (
                          <>
                            <td className="border-l border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSFO, 1, 1)}
                            </td>
                            <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSFOPrice, 2, 2)}
                            </td>
                            <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSFOSum, 0, 0)}
                            </td>
                          </>
                        )}

                        {/* LSGO Columns */}
                        {!hiddenFuelTypes.LSGO && (
                          <>
                            <td className="border-l border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSGO, 1, 1)}
                            </td>
                            <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSGOPrice, 2, 2)}
                            </td>
                            <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSGOSum, 0, 0)}
                            </td>
                          </>
                        )}

                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(offhire.Lumpsum, 0, 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(offhire.Total, 0, 0)}
                        </td>
                        <td className="px-2.5 py-2 text-left text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {offhire.Description}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </>
            )}
          </table>
        </div>
      </div>

      {/* No Data State */}
      {!isLoading && offhires.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No breakdown data available</p>
        </div>
      )}
    </div>
  );
}
